// httpClient.ts
import axios, { AxiosResponse, AxiosError, AxiosRequestConfig } from 'axios';
import config from '../Config/config.json'

const baseURL = config.apiBaseUrl; // Replace with your API base URL
const httpClient = axios.create({
  baseURL,
});

let isRefreshing = false;
let failedQueue: any[] = [];

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });

  failedQueue = [];
};

httpClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("accessToken");

    if (token) {
      config.headers = config.headers || {};
      config.headers["Authorization"] = `Bearer ${token}`;
    }

    return config;
  },
  (error) => Promise.reject(error)
);

httpClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError<any>) => {
    const originalRequest: any = error.config;

    console.log('Error response:', error.response);

    if (error.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        return new Promise(function (resolve, reject) {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers["Authorization"] = "Bearer " + token;
            return httpClient(originalRequest);
          })
          .catch((err) => Promise.reject(err));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      const refreshToken = localStorage.getItem("refreshToken");

      try {
        console.log('Attempting to refresh token with refreshToken:', refreshToken);
        const response = await axios.post(`${baseURL}Vyapar/User/refresh-token`, {
          RefreshToken: refreshToken,
        });

        console.log('Refresh token response:', response);
        const newAccessToken = response.data.data.accessToken;

        localStorage.setItem("accessToken", newAccessToken);

        httpClient.defaults.headers.common[
          "Authorization"
        ] = `Bearer ${newAccessToken}`;

        processQueue(null, newAccessToken);

        originalRequest.headers["Authorization"] = `Bearer ${newAccessToken}`;

        return httpClient(originalRequest);
      } catch (err) {
        processQueue(err, null);

        localStorage.removeItem("accessToken");
        localStorage.removeItem("refreshToken");

        // window.location.href = "/login";

        return Promise.reject(err);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

// Add a function to set the token in the headers
// const setAuthToken = (token: string | null): void => {
//   if (token) {
//     httpClient.defaults.headers.common['Authorization'] = `Bearer ${token}`;
//   } else {
//     delete httpClient.defaults.headers.common['Authorization'];
//   }
// };

const get = async function <T>(url: string, config?: AxiosRequestConfig): Promise<T> {
  try {
    const response: AxiosResponse<T> = await httpClient.get<T>(url, config);
    return response.data
  } catch (error: any) {
    return error?.response?.data || error.message || 'An error occurred while fetching data.';
  }
};

const post = async function <T>(url: string, data: any, config?: AxiosRequestConfig): Promise<T> {
  try {
    const response: AxiosResponse<T> = await httpClient.post(url, data, config);
    return response.data;
  } catch (error) {
    console.error('Error posting data:', error);
    throw error;
  }
};

const del = async function <T>(url: string, config?: AxiosRequestConfig): Promise<T> {
  try {
    const response: AxiosResponse<T> = await httpClient.delete(url, config);
    return response.data;
  } catch (error) {
    console.error('Error deleting data:', error);
    throw error;
  }
};
const put = async function <T>(url: string, data: any, config?: AxiosRequestConfig): Promise<T> {
  try {
    const response: AxiosResponse<T> = await httpClient.put<T>(url, data, config);
    return response.data;
  } catch (error) {
    console.error('Error putting data:', error);
    throw error;
  }
};

// Dedicated function for posting FormData
const postFormData = async function <T>(
  url: string,
  formData: FormData, // Explicitly expect FormData
  config?: AxiosRequestConfig
): Promise<T> {
  try {
    const response: AxiosResponse<T> = await httpClient.post(url, formData, {
      headers: {
        "Content-Type": "multipart/form-data", // Set FormData-specific header
        ...config?.headers, // Merge custom headers if provided
      },
      ...config, // Spread other configurations
    });

    return response.data;
  } catch (error: any) {
    console.error("Error posting FormData:", error);
    return error.response?.data || error.message;
  }
};

const putFormData = async function <T>(
  url: string,
  formData: FormData, // Explicitly expect FormData
  config?: AxiosRequestConfig
): Promise<T> {
  try {
    const response: AxiosResponse<T> = await httpClient.put(url, formData, {
      headers: {
        "Content-Type": "multipart/form-data", // Set FormData-specific header
        ...config?.headers, // Merge custom headers if provided
      },
      ...config, // Spread other configurations
    });

    return response.data;
  } catch (error: any) {
    console.error("Error updating FormData:", error);
    return error.response?.data || error.message;
  }
};

const patch = async function <T>(url: string, data: any, config?: AxiosRequestConfig): Promise<T> {
  try {
    const response: AxiosResponse<T> = await httpClient.patch<T>(url, data, config);
    return response.data;
  } catch (error) {
    console.error('Error patching data:', error);
    throw error;
  }
};

const http = {
  get,
  post,
  del,
  put,
  // setAuthToken,
  postFormData,
  putFormData,
  patch
};

export default http;
